import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription, type PlanName, type PlanLimits } from "@/hooks/use-subscription";

const RANK: Record<PlanName, number> = { free: 0, pro: 1, premium: 2 };

export interface UsageCounts {
  saved_games: number;
  active_pools: number;
}

/**
 * Hook unificado: retorna o plano efetivo, os limites do plano, contagens
 * atuais de uso (jogos salvos / bolões ativos) e helpers `has(tier)` e
 * `canAddSavedGame()` / `canAddPool()` / `canAddParticipants(n)`.
 *
 * Usa `useSubscription` como fonte de verdade (price_id → plan), com fallback
 * em `profiles.plan` caso a assinatura ainda não esteja sincronizada.
 */
export function usePlanLimits() {
  const { user } = useAuth();
  const { plan: subPlan, limits, loading: subLoading, isActive } = useSubscription();
  const [usage, setUsage] = useState<UsageCounts>({ saved_games: 0, active_pools: 0 });
  const [usageLoading, setUsageLoading] = useState(true);

  const refreshUsage = useCallback(async () => {
    if (!user) {
      setUsage({ saved_games: 0, active_pools: 0 });
      setUsageLoading(false);
      return;
    }
    setUsageLoading(true);
    const [{ count: gamesCount }, { count: poolsCount }] = await Promise.all([
      supabase
        .from("saved_games")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id),
      supabase
        .from("pools")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .in("status", ["draft", "active"]),
    ]);
    setUsage({
      saved_games: gamesCount ?? 0,
      active_pools: poolsCount ?? 0,
    });
    setUsageLoading(false);
  }, [user]);

  useEffect(() => {
    refreshUsage();
  }, [refreshUsage]);

  const plan: PlanName = subPlan;

  const has = (tier: PlanName) => RANK[plan] >= RANK[tier];

  /** `null` = ilimitado. Retorna a quantidade máxima permitida. */
  const limitFor = (key: "saved_games_limit" | "active_pools_limit" | "pool_participants_limit"): number | null => {
    return limits?.[key] ?? null;
  };

  const canAddSavedGames = (n = 1): { ok: boolean; remaining: number | null } => {
    const lim = limitFor("saved_games_limit");
    if (lim === null) return { ok: true, remaining: null };
    const remaining = Math.max(0, lim - usage.saved_games);
    return { ok: usage.saved_games + n <= lim, remaining };
  };

  const canAddPool = (): { ok: boolean; remaining: number | null } => {
    const lim = limitFor("active_pools_limit");
    if (lim === null) return { ok: true, remaining: null };
    const remaining = Math.max(0, lim - usage.active_pools);
    return { ok: usage.active_pools < lim, remaining };
  };

  const canHaveParticipants = (current: number, addingShares = 1): { ok: boolean; max: number | null } => {
    const lim = limitFor("pool_participants_limit");
    if (lim === null) return { ok: true, max: null };
    return { ok: current + addingShares <= lim, max: lim };
  };

  return {
    loading: subLoading || usageLoading,
    plan,
    isPaid: plan !== "free",
    isActive,
    limits: limits as PlanLimits | null,
    usage,
    has,
    limitFor,
    canAddSavedGames,
    canAddPool,
    canHaveParticipants,
    refreshUsage,
  };
}